import { create } from "zustand";
import type { FiltersState } from "~/client/common/filters";
import { countAppliedFilters, filtersToParams } from "~/client/common/filters";
import type { ListSpacesParams } from "~/shared/space/space-list.params";

export type FilterToken =
  | { type: "space_type"; value: string }
  | { type: "privacy"; value: string }
  | { type: "noise"; value: string }
  | { type: "amenity"; value: string }
  | { type: "av"; value: string }
  | { type: "outdoor"; value: string }
  | { type: "window"; value: string }
  | { type: "price"; value: 1 | 2 | 3 | 4 }
  | { type: "google"; value: 3.5 | 4 | 4.2 | 4.5 }
  | {
    type: "michelin";
    value: "3 Stars" | "2 Stars" | "1 Star" | "Bib Gourmand";
  }
  | { type: "venue_type"; value: string }
  | { type: "venue_highlight"; value: string }
  | { type: "top_hospitality_groups" }
  | { type: "cuisine"; value: string }
  | { type: "neighborhood"; value: string }
  | { type: "venue_name"; value: string }
  | {
    type:
      | "standing_from"
      | "standing_to"
      | "seated_from"
      | "seated_to"
      | "f_and_b_minimum_from"
      | "f_and_b_minimum_to";
    value: number;
  }
  | { type: "bounds"; nw: string; se: string }
  | { type: "radius"; miles: number }
  | { type: "location"; address: string; lat?: number; lng?: number };

function createEmptyFilters(): FiltersState {
  return {
    location: "",
    locationPlaceLabel: undefined,
    vibe: "",
    locationLat: undefined,
    locationLng: undefined,
    boundsNwGeohash: undefined,
    boundsSeGeohash: undefined,
    capacityStandingMin: undefined,
    capacityStandingMax: undefined,
    capacitySeatedMin: undefined,
    capacitySeatedMax: undefined,
    fbMin: undefined,
    fbMax: undefined,
    venueName: "",
    googlePlaceId: undefined,
    radiusMiles: undefined,
    spaceTypes: {},
    privacyTypes: {},
    noisePrivacy: {},
    amenities: {},
    av: {},
    outdoor: {},
    windows: {},
    pricePoints: { 1: false, 2: false, 3: false, 4: false },
    google: { "4.5": false, "4.2": false, "4.0": false, "3.5": false },
    michelin: {
      "3 Stars": false,
      "2 Stars": false,
      "1 Star": false,
      "Bib Gourmand": false,
    },
    venueTypes: {},
    venueHighlights: { 'Notable Chef': false },
    editorialRecognition: {},
    topHospitalityGroups: false,
    cuisines: {},
    neighborhoods: {},
    labelToCuisineId: undefined,
    labelToVenueTypeId: undefined,
    labelToNeighborhoodId: undefined,
    venueTagLabelById: undefined,
  };
}

function applyTokenToFilters(
  base: FiltersState,
  token: FilterToken,
): FiltersState {
  const f = { ...base };
  switch (token.type) {
    case "space_type":
      f.spaceTypes = { ...f.spaceTypes, [token.value]: true };
      break;
    case "privacy":
      f.privacyTypes = { ...f.privacyTypes, [token.value]: true };
      break;
    case "noise":
      f.noisePrivacy = { ...f.noisePrivacy, [token.value]: true };
      break;
    case "amenity":
      f.amenities = { ...f.amenities, [token.value]: true };
      break;
    case "av":
      f.av = { ...f.av, [token.value]: true };
      break;
    case "outdoor":
      f.outdoor = { ...f.outdoor, [token.value]: true };
      break;
    case "window":
      f.windows = { ...f.windows, [token.value]: true };
      break;
    case "price":
      f.pricePoints = { ...f.pricePoints, [token.value]: true } as Record<
        number,
        boolean
      >;
      break;
    case "google": {
      const key = String(token.value) as keyof FiltersState["google"];
      f.google = { ...f.google, [key]: true } as Record<string, boolean>;
      break;
    }
    case "michelin":
      f.michelin = { ...(f.michelin as any), [token.value]: true } as any;
      break;
    case "venue_type":
      f.venueTypes = { ...f.venueTypes, [token.value]: true };
      break;
    case "venue_highlight":
      f.venueHighlights = { ...f.venueHighlights, [token.value]: true };
      break;
    case "top_hospitality_groups":
      f.topHospitalityGroups = true;
      break;
    case "cuisine":
      f.cuisines = { ...f.cuisines, [token.value]: true };
      break;
    case "neighborhood":
      f.neighborhoods = { ...f.neighborhoods, [token.value]: true };
      break;
    case "venue_name":
      f.venueName = token.value;
      break;
    case "standing_from":
      f.capacityStandingMin = token.value;
      break;
    case "standing_to":
      f.capacityStandingMax = token.value;
      break;
    case "seated_from":
      f.capacitySeatedMin = token.value;
      break;
    case "seated_to":
      f.capacitySeatedMax = token.value;
      break;
    case "f_and_b_minimum_from":
      f.fbMin = token.value;
      break;
    case "f_and_b_minimum_to":
      f.fbMax = token.value;
      break;
    case "bounds":
      f.boundsNwGeohash = token.nw;
      f.boundsSeGeohash = token.se;
      break;
    case "radius":
      f.radiusMiles = token.miles;
      break;
    case "location":
      f.location = token.address;
      f.locationPlaceLabel = undefined;
      f.locationLat = token.lat;
      f.locationLng = token.lng;
      break;
  }
  return f;
}

function exportFiltersToTokens(filters: FiltersState): FilterToken[] {
  const tokens: FilterToken[] = [];
  for (const [k, v] of Object.entries(filters.spaceTypes)) {
    if (v) tokens.push({ type: "space_type", value: k });
  }
  for (const [k, v] of Object.entries(filters.privacyTypes)) {
    if (v) tokens.push({ type: "privacy", value: k });
  }
  for (const [k, v] of Object.entries(filters.noisePrivacy)) {
    if (v) tokens.push({ type: "noise", value: k });
  }
  for (const [k, v] of Object.entries(filters.amenities)) {
    if (v) tokens.push({ type: "amenity", value: k });
  }
  for (const [k, v] of Object.entries(filters.av)) {
    if (v) tokens.push({ type: "av", value: k });
  }
  for (const [k, v] of Object.entries(filters.outdoor)) {
    if (v) tokens.push({ type: "outdoor", value: k });
  }
  for (const [k, v] of Object.entries(filters.windows)) {
    if (v) tokens.push({ type: "window", value: k });
  }
  for (const [k, v] of Object.entries(filters.venueTypes)) {
    if (v) tokens.push({ type: "venue_type", value: k });
  }
  for (const [k, v] of Object.entries(filters.venueHighlights || {})) {
    if (v) tokens.push({ type: "venue_highlight", value: k });
  }
  if (filters.topHospitalityGroups) {
    tokens.push({ type: "top_hospitality_groups" });
  }
  for (const [k, v] of Object.entries(filters.cuisines)) {
    if (v) tokens.push({ type: "cuisine", value: k });
  }
  for (const [k, v] of Object.entries(filters.neighborhoods)) {
    if (v) tokens.push({ type: "neighborhood", value: k });
  }
  ([1, 2, 3, 4] as const).forEach((n) => {
    if ((filters.pricePoints as any)[n]) {
      tokens.push({ type: "price", value: n });
    }
  });
  ([4.5, 4.2, 4.0, 3.5] as const).forEach((g) => {
    if (filters.google[String(g)]) tokens.push({ type: "google", value: g });
  });
  (["3 Stars", "2 Stars", "1 Star", "Bib Gourmand"] as const).forEach((m) => {
    if ((filters.michelin as any)[m]) {
      tokens.push({ type: "michelin", value: m });
    }
  });
  if (filters.venueName && filters.venueName.trim()) {
    tokens.push({ type: "venue_name", value: filters.venueName });
  }
  if (typeof filters.capacityStandingMin === "number") {
    tokens.push({ type: "standing_from", value: filters.capacityStandingMin });
  }
  if (typeof filters.capacityStandingMax === "number") {
    tokens.push({ type: "standing_to", value: filters.capacityStandingMax });
  }
  if (typeof filters.capacitySeatedMin === "number") {
    tokens.push({ type: "seated_from", value: filters.capacitySeatedMin });
  }
  if (typeof filters.capacitySeatedMax === "number") {
    tokens.push({ type: "seated_to", value: filters.capacitySeatedMax });
  }
  if (typeof filters.fbMin === "number") {
    tokens.push({ type: "f_and_b_minimum_from", value: filters.fbMin });
  }
  if (typeof filters.fbMax === "number") {
    tokens.push({ type: "f_and_b_minimum_to", value: filters.fbMax });
  }
  if (filters.boundsNwGeohash && filters.boundsSeGeohash) {
    tokens.push({
      type: "bounds",
      nw: filters.boundsNwGeohash,
      se: filters.boundsSeGeohash,
    });
  }
  if (typeof filters.radiusMiles === "number") {
    tokens.push({ type: "radius", miles: filters.radiusMiles });
  }
  if (filters.location) {
    tokens.push({
      type: "location",
      address: filters.location,
      lat: filters.locationLat,
      lng: filters.locationLng,
    });
  }
  return tokens;
}

type FiltersStoreState = {
  filters: FiltersState;
  tokens: FilterToken[];
  activeVenueGroupId?: number;
  lastAppliedAt: number | null;
  isCreatingGhostVenue: boolean;
  resetTrigger: number;
  // State setters
  setFilters: (
    updater: FiltersState | ((prev: FiltersState) => FiltersState),
  ) => void;
  reset: () => void;
  apply: () => void;
  setVenueGroupId: (id?: number) => void;
  setLabelMappings: (
    mappings: Partial<
      Pick<
        FiltersState,
        "labelToCuisineId" | "labelToVenueTypeId" | "labelToNeighborhoodId"
      >
    >,
  ) => void;
  setBounds: (nw?: string, se?: string) => void;
  setLocation: (address: string, lat?: number, lng?: number) => void;
  setRadius: (miles?: number) => void;
  setIsCreatingGhostVenue: (creating: boolean) => void;
  // Tokens
  setTokens: (tokens: FilterToken[], options?: { merge?: boolean }) => void;
  exportTokens: () => FilterToken[];
  // Derived helpers
  getAppliedCount: () => number;
  getParams: () => ListSpacesParams;
};

export const useFiltersStore = create<FiltersStoreState>((set, get) => ({
  filters: createEmptyFilters(),
  tokens: [],
  activeVenueGroupId: undefined,
  lastAppliedAt: null,
  isCreatingGhostVenue: false,
  resetTrigger: 0,

  setFilters: (updater) =>
    set((state) => {
      const next = typeof updater === "function"
        ? (updater as (prev: FiltersState) => FiltersState)(state.filters)
        : (updater as FiltersState);
      return { filters: next };
    }),

  reset: () =>
    set((state) => ({
      filters: createEmptyFilters(),
      tokens: [],
      lastAppliedAt: null,
      isCreatingGhostVenue: false,
      resetTrigger: state.resetTrigger + 1,
    })),
  apply: () => set({ lastAppliedAt: Date.now() }),
  setVenueGroupId: (id) => set({ activeVenueGroupId: id }),
  setLabelMappings: (mappings) =>
    set((state) => ({ filters: { ...state.filters, ...mappings } })),
  setBounds: (nw, se) =>
    set((state) => ({
      filters: { ...state.filters, boundsNwGeohash: nw, boundsSeGeohash: se },
    })),
  setLocation: (address, lat, lng) =>
    set((state) => ({
      filters: {
        ...state.filters,
        location: address,
        locationLat: lat,
        locationLng: lng,
      },
    })),
  setRadius: (miles) =>
    set((state) => ({ filters: { ...state.filters, radiusMiles: miles } })),
  setIsCreatingGhostVenue: (creating) =>
    set({ isCreatingGhostVenue: creating }),

  setTokens: (tokens, options) =>
    set((state) => {
      const merge = !!options?.merge;
      const base = merge ? state.filters : createEmptyFilters();
      const merged = tokens.reduce(applyTokenToFilters, base);
      return { filters: merged, tokens };
    }),
  exportTokens: () => {
    const { filters } = get();
    return exportFiltersToTokens(filters);
  },

  getAppliedCount: () => countAppliedFilters(get().filters),
  getParams: () => {
    const { filters, activeVenueGroupId } = get();
    return filtersToParams(filters, activeVenueGroupId);
  },
}));
